<template>
  <div class="upload-container">
    <h2>股东名册上传</h2>
    
    <!-- 上传入口：点击打开弹窗 -->
    <div class="upload-card">
      <el-button type="primary" size="large" @click="uploadDialogVisible = true">
        <el-icon><UploadFilled /></el-icon>
        上传股东名册
      </el-button>
      <div class="tip-text">
        <el-icon><InfoFilled /></el-icon>
        支持直接上传券商/登记公司导出的<strong>原始名册</strong>（系统自动识别表头并清洗）；或标准表。上传前可先解析预览，确认后再入库。
      </div>
    </div>

    <!-- 上传弹窗：填股票代码、数据更新日期 → 选择文件 → 解析预览 → 确认导入 -->
    <el-dialog v-model="uploadDialogVisible" title="上传股东名册" width="800px" :close-on-click-modal="false" @close="resetUploadDialog" class="upload-dialog">
      <div class="dialog-body-scroll">
      <el-form label-width="120px" class="dialog-form">
        <el-form-item label="股票代码" required>
          <el-select
            v-model="dialogForm.stock_code"
            placeholder="请选择股票代码"
            style="width: 320px;"
            filterable
          >
            <el-option
              v-for="item in tenantOptions"
              :key="item.stock_code"
              :label="item.label"
              :value="item.stock_code"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="选择文件" required>
          <el-upload
            ref="dialogUploadRef"
            :auto-upload="false"
            :show-file-list="false"
            :limit="1"
            :disabled="parseLoading"
            :on-change="onDialogFileChange"
            :on-exceed="() => ElMessage.warning('仅支持单文件')"
            accept=".xlsx,.xls"
          >
            <el-button type="default" :loading="parseLoading">{{ parseLoading ? '解析中…' : '浏览本地文件' }}</el-button>
          </el-upload>
        </el-form-item>
        <el-form-item v-if="selectedFile" label="已选文件">
          <div class="selected-file-name" :title="selectedFile.name">{{ selectedFile.name }}</div>
        </el-form-item>
        <el-form-item v-if="selectedFile && parseDateRequiredMessage" label=" ">
          <div style="font-size: 12px; color: #e6a23c; line-height: 1.5;">
            {{ parseDateRequiredMessage }}
          </div>
        </el-form-item>
        <el-form-item v-if="selectedFile" label="数据更新日期" required>
          <el-date-picker
            v-model="dialogForm.data_update_date"
            type="date"
            value-format="YYYY-MM-DD"
            placeholder="可手动选择；若未识别到日期则必须填写"
            clearable
            style="width: 320px;"
            @change="onManualDateChange"
          />
          <div v-if="dialogForm.data_update_date" style="font-size: 12px; color: #67c23a; margin-top: 4px;">
            当前日期：{{ dialogForm.data_update_date }}（可继续修改）
          </div>
        </el-form-item>
      </el-form>
      <!-- 解析预览结果（选择文件并解析后显示） -->
      <div v-if="parsePreview" class="parse-preview">
        <el-alert :title="parsePreview.message" :type="parsePreview.fail_count > 0 ? 'warning' : 'success'" show-icon style="margin-bottom: 12px;" />
        <div class="summary-cards" v-if="parsePreview.batch_summary">
          <div class="summary-card"><span class="label">总笔数</span><span class="value">{{ parsePreview.batch_summary.total }}</span></div>
          <div class="summary-card success"><span class="label">校验通过</span><span class="value">{{ parsePreview.batch_summary.success }}</span></div>
          <div class="summary-card danger"><span class="label">失败</span><span class="value">{{ parsePreview.batch_summary.fail }}</span></div>
        </div>
        <div v-if="parsePreview.detected_columns && parsePreview.detected_columns.length" style="margin-top: 10px; font-size: 12px; color: #606266;">
          <span class="label">识别到的表头：</span>{{ parsePreview.detected_columns.join('、') }}
        </div>
        <div v-if="parsePreview" style="margin-top: 12px;">
          <div class="section-title">解析明细（所有有效数据，可核对解析是否正确）</div>
          <el-table v-if="parsePreview.parsed_sample && parsePreview.parsed_sample.length" :data="parsePreview.parsed_sample" border stripe size="small" max-height="320" style="width: 100%">
            <el-table-column prop="row_num" label="行号" width="64" />
            <el-table-column prop="holder_name" label="持有人名称" min-width="140" show-overflow-tooltip />
            <el-table-column prop="securities_account_no" label="证券账户号码" width="130" show-overflow-tooltip>
              <template #default="{ row }">{{ row.securities_account_no != null && row.securities_account_no !== '' ? String(row.securities_account_no) : '—' }}</template>
            </el-table-column>
            <el-table-column prop="contact_phone" label="联系电话" width="130" show-overflow-tooltip>
              <template #default="{ row }">{{ row.contact_phone || '—' }}</template>
            </el-table-column>
            <el-table-column prop="contact_address" label="通讯地址" min-width="200" show-overflow-tooltip>
              <template #default="{ row }">{{ row.contact_address || '—' }}</template>
            </el-table-column>
            <el-table-column prop="hold_quantity" label="持有数量" width="100" />
            <el-table-column prop="hold_ratio" label="持有比例" width="90">
              <template #default="{ row }">{{ row.hold_ratio != null ? (row.hold_ratio * 100).toFixed(4) + '%' : '—' }}</template>
            </el-table-column>
            <el-table-column prop="status" label="校验" width="70">
              <template #default="{ row }">
                <el-tag v-if="row.status === 'success'" type="success" size="small">通过</el-tag>
                <el-tag v-else type="danger" size="small">失败</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="失败原因" min-width="180">
              <template #default="{ row }">
                <template v-if="row.column_errors && row.column_errors.length">
                  <span v-for="(e, i) in row.column_errors" :key="i" class="err-item">{{ e.column_name }}：{{ e.error_reason }}</span>
                </template>
                <span v-else class="text-muted">—</span>
              </template>
            </el-table-column>
          </el-table>
          <div v-else class="parse-no-detail">暂无解析明细数据</div>
        </div>
      </div>
      </div>
      <template #footer>
        <span>
          <el-button @click="uploadDialogVisible = false">取消</el-button>
          <el-button type="primary" :loading="confirmLoading" :disabled="!canConfirmImport" @click="doConfirmImport">
            确认导入（写入数据库）
          </el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 上传结果 -->
    <div class="result-area" v-if="uploadResult">
      <el-alert :title="uploadResult.message" :type="uploadResult.type" show-icon closable @close="uploadResult = null" />
      <!-- 本批次概览 -->
      <div class="batch-summary" v-if="uploadResult.batch_summary">
        <h3 class="section-title">本批次概览</h3>
        <div class="summary-cards">
          <div class="summary-card"><span class="label">数据更新日期</span><span class="value">{{ uploadResult.batch_summary.data_update_date }}</span></div>
          <div class="summary-card"><span class="label">股票代码</span><span class="value">{{ uploadResult.batch_summary.stock_code || '—' }}</span></div>
          <div class="summary-card"><span class="label">批次ID</span><span class="value text-ellipsis">{{ uploadResult.batch_summary.batch_id }}</span></div>
          <div class="summary-card"><span class="label">总笔数</span><span class="value">{{ uploadResult.batch_summary.total }}</span></div>
          <div class="summary-card success"><span class="label">成功</span><span class="value">{{ uploadResult.batch_summary.success }}</span></div>
          <div class="summary-card info"><span class="label">跳过</span><span class="value">{{ uploadResult.batch_summary.skip }}</span></div>
          <div class="summary-card danger"><span class="label">失败</span><span class="value">{{ uploadResult.batch_summary.fail }}</span></div>
        </div>
      </div>
      <!-- 校验与数据质量 -->
      <div class="validation-area" v-if="uploadResult.validation && (uploadResult.validation.empty_required_count > 0)">
        <el-collapse>
          <el-collapse-item title="校验与数据质量" name="validation">
            <div>必填字段为空行数：<strong>{{ uploadResult.validation.empty_required_count }}</strong></div>
          </el-collapse-item>
        </el-collapse>
      </div>
      <!-- 失败行明细（可筛选仅失败） -->
      <div class="row-result-area" style="margin-top: 20px;" v-if="uploadResult.rowResults && uploadResult.rowResults.length">
        <h3 class="section-title">行级导入结果明细</h3>
        <div class="filter-row">
          <el-radio-group v-model="resultFilter" size="small">
            <el-radio-button value="all">全部</el-radio-button>
            <el-radio-button value="fail">仅失败</el-radio-button>
          </el-radio-group>
        </div>
        <el-table :data="filteredRowResults" border stripe style="width: 100%">
          <el-table-column prop="row_num" label="行号" width="80" />
          <el-table-column prop="status" label="状态" width="100">
            <template #default="{ row }">
              <el-tag v-if="row.status === 'success'" type="success">成功</el-tag>
              <el-tag v-if="row.status === 'skip'" type="info">跳过</el-tag>
              <el-tag v-if="row.status === 'fail'" type="danger">失败</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="message" label="行级说明" min-width="200" />
          <el-table-column label="列级错误明细" min-width="300">
            <template #default="{ row }">
              <div v-if="row.column_errors && row.column_errors.length > 0">
                <div v-for="(err, idx) in row.column_errors" :key="idx" class="column-error-item">
                  <span class="error-column">{{ err.column_name }}</span>：{{ err.error_reason }}
                </div>
              </div>
              <div v-else class="no-error">无列级错误</div>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <!-- 本批数据预览 -->
      <div class="preview-area" v-if="uploadResult.preview_rows && uploadResult.preview_rows.length">
        <h3 class="section-title">本批数据预览（成功的前 20 条）</h3>
        <el-table :data="uploadResult.preview_rows" border stripe size="small" style="width: 100%">
          <el-table-column prop="serial_number" label="序号" width="70" />
          <el-table-column prop="holder_name" label="持有人名称" min-width="120" />
          <el-table-column prop="securities_account_no" label="证券账户号码" width="130">
          <template #default="{ row }">{{ row.securities_account_no != null && row.securities_account_no !== '' ? String(row.securities_account_no) : '—' }}</template>
        </el-table-column>
          <el-table-column prop="hold_quantity" label="持有数量" width="110" />
          <el-table-column prop="hold_ratio" label="持有比例" width="100">
            <template #default="{ row }">{{ row.hold_ratio != null ? (row.hold_ratio * 100).toFixed(4) + '%' : '—' }}</template>
          </el-table-column>
          <el-table-column prop="data_update_date" label="更新日期" width="110" />
        </el-table>
        <el-button type="primary" link @click="viewShareholderList(uploadResult.data_batch_id)" style="margin-top: 8px;">查看本批全部</el-button>
      </div>
    </div>

    <!-- 历史上传记录 -->
    <div class="history-area" style="margin-top: 30px;">
      <div class="history-header">
        <h3 class="section-title">历史上传记录</h3>
        <el-button type="text" @click="showAllHistory = !showAllHistory" v-if="historyList.length > 3">
          {{ showAllHistory ? '收起' : '展开' }}所有批次
        </el-button>
      </div>
      <el-table :data="showAllHistory ? historyList : historyList.slice(0, 3)" border stripe style="width: 100%">
        <el-table-column prop="data_batch_id" label="批次ID" min-width="200" />
        <el-table-column prop="data_update_date" label="更新日期" />
        <el-table-column prop="stock_code" label="股票代码" />
        <el-table-column prop="total_count" label="总条数" />
        <el-table-column prop="success_count" label="成功条数" />
        <el-table-column prop="fail_count" label="失败条数" />
        <el-table-column label="操作" width="160">
          <template #default="{ row }">
            <el-button type="primary" link @click="viewShareholderList(row.data_batch_id)">查看数据</el-button>
            <el-button type="danger" link @click="deleteBatch(row.data_batch_id)">删除本批</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div v-if="historyList.length > 3 && !showAllHistory" class="history-tip">
        已隐藏{{ historyList.length - 3 }}个历史批次，点击展开查看全部
      </div>
    </div>

    <!-- 股东数据列表 -->
    <div class="data-list-area" style="margin-top: 30px;" v-if="showDataList">
      <h3 class="section-title">股东数据列表</h3>
      <div class="search-bar">
        <HolderSuggestInput v-model="searchParams.holder_name" type="raw" placeholder="持有人名称（汉字/拼音/首字母）" style="width: 240px; margin-right: 10px;" />
        <el-input v-model="searchParams.stock_code" placeholder="请输入股票代码" style="width: 200px; margin-right: 10px;" />
        <el-button type="primary" @click="getShareholderList">搜索</el-button>
        <el-button type="default" @click="resetSearch">重置</el-button>
      </div>
      <el-table :data="shareholderList" border stripe style="width: 100%; margin-top: 10px;" v-loading="isLoading">
        <el-table-column prop="serial_number" label="序号" />
        <el-table-column prop="holder_name" label="持有人名称" min-width="150" />
        <el-table-column prop="securities_account_no" label="证券账户号码">
          <template #default="{ row }">{{ row.securities_account_no != null && row.securities_account_no !== '' ? String(row.securities_account_no) : '—' }}</template>
        </el-table-column>
        <el-table-column prop="id_card_no" label="证件号码" />
        <el-table-column prop="holder_type" label="持有人类别" />
        <el-table-column prop="hold_quantity" label="持有数量" />
        <el-table-column prop="hold_ratio" label="持有比例" />
        <el-table-column prop="data_update_date" label="更新日期" />
        <el-table-column prop="data_batch_id" label="批次ID" />
      </el-table>
      <el-pagination @size-change="handleSizeChange" @current-change="handleCurrentChange" :current-page="pagination.page" :page-sizes="[10, 20, 50]" :page-size="pagination.page_size" layout="total, sizes, prev, pager, next, jumper" :total="pagination.total_count" style="margin-top: 10px; text-align: right;"></el-pagination>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { UploadFilled, InfoFilled } from '@element-plus/icons-vue'
import axios from 'axios'
import HolderSuggestInput from '@/components/HolderSuggestInput.vue'

// 不再覆盖全局 baseURL，与 main.js 一致，部署时由 VUE_APP_API_BASE_URL 控制

// 上传弹窗
const uploadDialogVisible = ref(false)
const dialogForm = ref({ data_update_date: '', stock_code: '' })
const selectedFile = ref(null)
const parsePreview = ref(null)
const confirmLoading = ref(false)
const parseLoading = ref(false)
const dialogUploadRef = ref(null)
const tenantOptions = ref([])
const tenantDefaultStockCode = ref('')
const parseDateRequiredMessage = ref('')
const isDateRequiredError = (msg) =>
  typeof msg === 'string' && msg.includes('无法确定数据更新日期')

const canConfirmImport = computed(() => {
  if (!parsePreview.value || !selectedFile.value) return false
  if (parseDateRequiredMessage.value && !dialogForm.value.data_update_date) return false
  return true
})

async function loadTenantOptions() {
  try {
    const res = await axios.get('/api/upload/tenant_options/', { withCredentials: true })
    if (res.data?.status === 'ok') {
      const data = res.data.data || {}
      tenantOptions.value = Array.isArray(data.options) ? data.options : []
      tenantDefaultStockCode.value =
        data.default_stock_code || tenantOptions.value[0]?.stock_code || ''
      if (!dialogForm.value.stock_code) {
        dialogForm.value.stock_code = tenantDefaultStockCode.value
      }
    }
  } catch (err) {
    ElMessage.error(err.response?.data?.message || '获取股票代码下拉失败')
  }
}

function resetUploadDialog() {
  dialogForm.value = {
    data_update_date: '',
    stock_code: tenantDefaultStockCode.value || dialogForm.value.stock_code || '',
  }
  selectedFile.value = null
  parsePreview.value = null
  parseDateRequiredMessage.value = ''
  if (dialogUploadRef.value) {
    try {
      dialogUploadRef.value.clearFiles()
    } catch (_) {
      /* clearFiles 可能不可用，忽略 */
    }
  }
}

async function onDialogFileChange(file) {
  if (!file || !file.raw) return
  const isExcel = file.raw.name.endsWith('.xlsx') || file.raw.name.endsWith('.xls')
  if (!isExcel) { ElMessage.error('请选择 .xlsx 或 .xls 文件'); return }
  if (file.raw.size / 1024 / 1024 >= 10) { ElMessage.error('文件大小不能超过 10MB'); return }
  selectedFile.value = file.raw
  await doParseOnly()
}

async function doParseOnly() {
  if (!selectedFile.value) return
  parseLoading.value = true
  parsePreview.value = null
  parseDateRequiredMessage.value = ''
  try {
    const form = new FormData()
    form.append('file', selectedFile.value)
    form.append('stock_code', dialogForm.value.stock_code.trim())
    if (dialogForm.value.data_update_date) form.append('data_update_date', dialogForm.value.data_update_date)
    form.append('dry_run', '1')
    const res = await axios.post('/api/upload/shareholder/', form, {
      withCredentials: true,
      headers: { 'X-CSRFToken': getCsrfToken() },
    })
    if (res.data.status === 'ok') {
      parsePreview.value = res.data
    } else {
      const msg = res.data.message || '解析失败'
      if (isDateRequiredError(msg)) {
        parseDateRequiredMessage.value = msg
      } else {
        ElMessage.error(msg)
      }
    }
  } catch (err) {
    const msg = err.response?.data?.message || err.message || '解析请求失败'
    if (isDateRequiredError(msg)) {
      parseDateRequiredMessage.value = msg
    } else {
      ElMessage.error(msg)
    }
  } finally {
    parseLoading.value = false
  }
}

async function onManualDateChange() {
  if (!selectedFile.value) return
  if (!dialogForm.value.data_update_date) {
    parsePreview.value = null
    return
  }
  await doParseOnly()
}

async function doConfirmImport() {
  if (!selectedFile.value || !parsePreview.value) return
  confirmLoading.value = true
  try {
    const form = new FormData()
    form.append('file', selectedFile.value)
    form.append('stock_code', dialogForm.value.stock_code.trim())
    if (dialogForm.value.data_update_date) form.append('data_update_date', dialogForm.value.data_update_date)
    form.append('dry_run', '0')
    const res = await axios.post('/api/upload/shareholder/', form, {
      withCredentials: true,
      headers: { 'X-CSRFToken': getCsrfToken() },
    })
    if (res.data.status === 'ok') {
      ElMessage.success('导入成功')
      uploadDialogVisible.value = false
      uploadResult.value = {
        message: res.data.message,
        type: 'success',
        successCount: res.data.success_count,
        skipCount: res.data.skip_count,
        failCount: res.data.fail_count,
        rowResults: res.data.row_results || [],
        data_batch_id: res.data.data_batch_id,
        batch_summary: res.data.batch_summary || null,
        validation: res.data.validation || null,
        preview_rows: res.data.preview_rows || []
      }
      getUploadHistory()
      searchParams.value.data_batch_id = res.data.data_batch_id
      getShareholderList()
    } else {
      ElMessage.error(res.data.message || '导入失败')
    }
  } catch (err) {
    ElMessage.error(err.response?.data?.message || err.message || '导入请求失败')
  } finally {
    confirmLoading.value = false
  }
}

async function deleteBatch(batchId) {
  try {
    await ElMessageBox.confirm('删除本批后，该批次数据将不可恢复；股权穿透等页面该日期将不再出现。是否继续？', '确认删除', {
      confirmButtonText: '删除',
      cancelButtonText: '取消',
      type: 'warning'
    })
  } catch (_) { return }
  try {
    const res = await axios.post('/api/upload/shareholder/delete_batch/', { data_batch_id: batchId }, { withCredentials: true })
    if (res.data.status === 'ok') {
      ElMessage.success(res.data.message || '已删除')
      getUploadHistory()
      if (searchParams.value.data_batch_id === batchId) {
        searchParams.value.data_batch_id = ''
        shareholderList.value = []
      }
    } else {
      ElMessage.error(res.data.message || '删除失败')
    }
  } catch (err) {
    ElMessage.error(err.response?.data?.message || err.message || '删除请求失败')
  }
}

// 行级结果筛选
const resultFilter = ref('all')
const filteredRowResults = computed(() => {
  const list = uploadResult.value?.rowResults || []
  if (resultFilter.value === 'fail') return list.filter(r => r.status === 'fail')
  return list
})

// 状态管理
const isLoading = ref(false)
const uploadResult = ref(null)
const historyList = ref([])
const showAllHistory = ref(false)
const latestBatchId = ref('')
const showDataList = ref(false)
const shareholderList = ref([])
const searchParams = ref({ holder_name: '', stock_code: '', data_batch_id: '' })
const pagination = ref({ page: 1, page_size: 10, total_count: 0 })

// 获取CSRF Token
function getCsrfToken() {
  const cookieValue = document.cookie.split('; ').find(row => row.startsWith('csrftoken='))?.split('=')[1]
  if (!cookieValue) { const metaTag = document.querySelector('meta[name="csrf-token"]'); return metaTag ? metaTag.content : '' }
  return cookieValue || ''
}

function getUploadHistory() {
  axios.get('/api/upload/history/', { withCredentials: true }).then(res => {
    if (res.data.status === 'ok') {
      historyList.value = res.data.data
      if (historyList.value.length > 0) {
        latestBatchId.value = historyList.value[0].data_batch_id
        searchParams.value.data_batch_id = latestBatchId.value
        showDataList.value = true
        getShareholderList()
      }
    }
  }).catch(err => { ElMessage.error('获取历史记录失败：' + err.message) })
}

function getShareholderList() {
  isLoading.value = true
  axios.get('/api/shareholder/list/', {
    params: { page: pagination.value.page, page_size: pagination.value.page_size, holder_name: searchParams.value.holder_name, stock_code: searchParams.value.stock_code, data_batch_id: searchParams.value.data_batch_id },
    withCredentials: true
  }).then(res => {
    if (res.data.status === 'ok') { shareholderList.value = res.data.data; pagination.value.total_count = res.data.total_count }
  }).catch(err => { ElMessage.error('获取数据列表失败：' + err.message) }).finally(() => { isLoading.value = false })
}

function viewShareholderList(batchId) { searchParams.value.data_batch_id = batchId; showDataList.value = true; getShareholderList() }
function handleSizeChange(val) { pagination.value.page_size = val; pagination.value.page = 1; getShareholderList() }
function handleCurrentChange(val) { pagination.value.page = val; getShareholderList() }
function resetSearch() { searchParams.value = { holder_name: '', stock_code: '', data_batch_id: latestBatchId.value }; pagination.value.page = 1; getShareholderList() }

onMounted(async () => {
  await loadTenantOptions()
  getUploadHistory()
})
</script>

<style scoped>
.upload-container { padding: 20px; }
.upload-card { background: #fff; padding: 30px; border-radius: 8px; box-shadow: 0 2px 12px rgba(0,0,0,0.1); margin-bottom: 20px; }
.tip-text { color: #606266; font-size: 14px; display: flex; align-items: center; gap: 5px; margin-top: 10px; }
.progress-area { margin-bottom: 20px; }
.progress-text { margin-top: 10px; color: #606266; }
.highlight { color: #409eff; font-weight: bold; }
.section-title { margin: 0 0 15px 0; color: #303133; font-size: 16px; font-weight: 600; }
.column-error-item { color: #f56c6c; font-size: 13px; margin: 2px 0; }
.error-column { font-weight: bold; }
.no-error { color: #67c23a; font-size: 13px; }
.history-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px; }
.history-tip { color: #606266; font-size: 14px; margin-top: 10px; text-align: right; }
.search-bar { margin-bottom: 10px; }
.upload-form-row { display: flex; flex-wrap: wrap; gap: 16px; margin-bottom: 16px; align-items: center; }
.summary-cards { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 8px; }
.summary-card { background: #f5f7fa; padding: 10px 14px; border-radius: 8px; min-width: 100px; }
.summary-card .label { display: block; font-size: 12px; color: #909399; margin-bottom: 4px; }
.summary-card .value { font-size: 15px; font-weight: 600; color: #303133; }
.summary-card .value.text-ellipsis { max-width: 180px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: inline-block; }
.summary-card.success .value { color: #67c23a; }
.summary-card.info .value { color: #909399; }
.summary-card.danger .value { color: #f56c6c; }
.filter-row { margin-bottom: 10px; }
.tip-text strong { color: #303133; }
.parse-preview .err-item { display: block; font-size: 12px; color: #f56c6c; margin: 2px 0; }
.parse-preview .text-muted { color: #909399; font-size: 12px; }
.dialog-body-scroll { max-height: 70vh; overflow-y: auto; padding-right: 4px; }
.parse-no-detail { padding: 12px; color: #909399; font-size: 13px; }
.selected-file-name {
  width: 100%;
  background: #f5f7fa;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 8px 10px;
  font-size: 13px;
  color: #303133;
  line-height: 1.4;
  white-space: normal;
  word-break: break-all;
}
</style>